/**
 * MacroWizard Component
 * Main orchestrator for step-by-step macro creation wizard
 */

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Plus, Sparkles, RefreshCw } from 'lucide-react'
import { Macro, WizardState, Platform } from '../../types/macros'
import { buildMacroFromWizard, generateMacroId, MACRO_TEMPLATES } from '../../utils/macroHelpers'

// Import step components
import PlatformStep from './steps/PlatformStep.tsx'
import ContextStep from './steps/ContextStep'
import PatternSelectionStep from './steps/PatternSelectionStep'
import TemplateStep from './steps/TemplateStep'
import DataKeyStep from './steps/DataKeyStep'
import MissingDataStep from './steps/MissingDataStep'
import PreviewStep from './steps/PreviewStep'
import AdvancedStep from './steps/AdvancedStep'

// Import pattern-specific step components
import SimplePatternStep from './steps/pattern-steps/SimplePatternStep'
import FallbackPatternStep from './steps/pattern-steps/FallbackPatternStep'
import ConditionalPatternStep from './steps/pattern-steps/ConditionalPatternStep'
import ConditionalElsePatternStep from './steps/pattern-steps/ConditionalElsePatternStep'
import AdvancedPatternStep from './steps/pattern-steps/AdvancedPatternStep'

interface MacroWizardProps {
  onAddMacro: (macro: Macro) => void
}

const MacroWizard: React.FC<MacroWizardProps> = ({ onAddMacro }) => {
  // Wizard state
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 1,
    platform: null,
    messageContext: null,
    macroPattern: null,
    startWithTemplate: false,
    selectedTemplate: null,
    dataKey: '',
    missingDataIntent: 'none',
    fallbackValue: '',
    conditionalContent: '',
    elseContent: '',
    description: '',
    advancedMode: false,
    customSyntax: '',
    // Pattern-specific data structure
    patternData: {
      simple: {
        dataKey: ''
      },
      fallback: {
        dataKey: '',
        fallbackValue: ''
      },
      conditional: {
        dataKey: '',
        conditionalContent: ''
      },
      conditionalElse: {
        dataKey: '',
        conditionalContent: '',
        elseContent: ''
      },
      advanced: {
        mode: 'guided',
        customSyntax: '',
        guidedConfig: {
          dataKeys: [''],
          operators: [],
          conditions: []
        }
      }
    }
  })

  const updateState = (updates: Partial<WizardState>) => {
    setWizardState(prev => ({ ...prev, ...updates }))
  }

  // Navigation handlers
  const goToStep = (step: number) => {
    updateState({ currentStep: step })
  }

  const resetWizard = () => {
    setWizardState({
      currentStep: 1,
      platform: null,
      messageContext: null,
      macroPattern: null,
      startWithTemplate: false,
      selectedTemplate: null,
      dataKey: '',
      missingDataIntent: 'none',
      fallbackValue: '',
      conditionalContent: '',
      elseContent: '',
      description: '',
      advancedMode: false,
      customSyntax: '',
      // Reset pattern-specific data structure
      patternData: {
        simple: {
          dataKey: ''
        },
        fallback: {
          dataKey: '',
          fallbackValue: ''
        },
        conditional: {
          dataKey: '',
          conditionalContent: ''
        },
        conditionalElse: {
          dataKey: '',
          conditionalContent: '',
          elseContent: ''
        },
        advanced: {
          mode: 'guided',
          customSyntax: '',
          guidedConfig: {
            dataKeys: [''],
            operators: [],
            conditions: []
          }
        }
      }
    })
  }

  const canGoNext = (): boolean => {
    switch (wizardState.currentStep) {
      case 1: // Platform selection
        return wizardState.platform !== null
      
      case 2: // Context selection
        return wizardState.messageContext !== null || wizardState.startWithTemplate
      
      case 3: // Pattern selection (for MomentPerks/PerksWall only)
        if (wizardState.platform === Platform.SHOPIFY || wizardState.startWithTemplate) {
          return true // Skip pattern selection for Shopify
        }
        return wizardState.macroPattern !== null
      
      case 4: // Template selection or Pattern-specific configuration
        if (wizardState.startWithTemplate) {
          return wizardState.selectedTemplate !== null
        }
        
        // For Shopify (legacy flow)
        if (wizardState.platform === Platform.SHOPIFY) {
          return wizardState.dataKey.trim() !== ''
        }
        
        // For pattern-specific flows
        if (wizardState.macroPattern) {
          switch (wizardState.macroPattern) {
            case 'SIMPLE':
              return wizardState.patternData.simple?.dataKey?.trim() !== ''
            case 'FALLBACK':
              return wizardState.patternData.fallback?.dataKey?.trim() !== '' && 
                     wizardState.patternData.fallback?.fallbackValue?.trim() !== ''
            case 'CONDITIONAL':
              return wizardState.patternData.conditional?.dataKey?.trim() !== '' && 
                     wizardState.patternData.conditional?.conditionalContent?.trim() !== ''
            case 'CONDITIONAL_ELSE':
              return wizardState.patternData.conditionalElse?.dataKey?.trim() !== '' && 
                     wizardState.patternData.conditionalElse?.conditionalContent?.trim() !== '' &&
                     wizardState.patternData.conditionalElse?.elseContent?.trim() !== ''
            case 'ADVANCED':
              return (wizardState.patternData.advanced?.customSyntax?.trim() !== '') ||
                     (wizardState.patternData.advanced?.mode === 'guided' &&
                      (wizardState.patternData.advanced?.guidedConfig?.dataKeys?.some((key: string) => key.trim() !== '') ?? false))
            default:
              return false
          }
        }
        
        return wizardState.dataKey.trim() !== ''
      
      case 5: // Missing data handling
        if (wizardState.missingDataIntent === 'fallback') {
          return wizardState.fallbackValue.trim() !== ''
        }
        if (wizardState.missingDataIntent === 'conditional') {
          return wizardState.conditionalContent.trim() !== ''
        }
        return true
      
      case 6: // Preview
        return true
      
      case 7: // Advanced
        return wizardState.customSyntax.trim() !== ''
      
      default:
        return false
    }
  }

  const handleNext = () => {
    if (!canGoNext()) return

    // Determine next step based on path
    if (wizardState.currentStep === 1) {
      goToStep(2) // Platform → Context
    } else if (wizardState.currentStep === 2) {
      // Context → Pattern (if MomentPerks/PerksWall) or DataKey (if Shopify or template mode)
      if (wizardState.platform === Platform.SHOPIFY || wizardState.startWithTemplate) {
        goToStep(4) // Skip pattern selection for Shopify
      } else {
        goToStep(3) // Go to pattern selection
      }
    } else if (wizardState.currentStep === 3) {
      goToStep(4) // Pattern → DataKey or Template
    } else if (wizardState.currentStep === 4) {
      if (wizardState.startWithTemplate && wizardState.selectedTemplate) {
        loadTemplateAndGoToPreview()
      } else if (wizardState.macroPattern !== null) {
        // Skip handling step if pattern was selected in questionnaire
        goToStep(6) // DataKey → Preview (skip step 5)
      } else {
        goToStep(5) // DataKey → Missing data handling (for Shopify)
      }
    } else if (wizardState.currentStep === 5) {
      goToStep(6) // Missing data → Preview
    } else if (wizardState.currentStep === 6 || wizardState.currentStep === 7) {
      handleAddMacro()
    }
  }

  const handleBack = () => {
    if (wizardState.currentStep === 7) {
      // From advanced, go back to preview
      updateState({ currentStep: 6, advancedMode: false })
    } else if (wizardState.currentStep === 6 && wizardState.macroPattern !== null) {
      // From Preview back to DataKey (skip Handling step if pattern was selected)
      goToStep(4)
    } else if (wizardState.currentStep === 4 && wizardState.platform !== Platform.SHOPIFY && !wizardState.startWithTemplate) {
      // From DataKey to Pattern (skip pattern for Shopify)
      goToStep(3)
    } else if (wizardState.currentStep === 4 && (wizardState.platform === Platform.SHOPIFY || wizardState.startWithTemplate)) {
      // From DataKey to Context (Shopify skips pattern)
      goToStep(2)
    } else if (wizardState.currentStep > 1) {
      goToStep(wizardState.currentStep - 1)
    }
  }

  // Template handling
  const loadTemplateAndGoToPreview = () => {
    if (!wizardState.selectedTemplate) return

    const template = MACRO_TEMPLATES.find(t => t.id === wizardState.selectedTemplate)
    if (!template || template.macros.length === 0) return

    // Load first macro from template
    const templateMacro = template.macros[0]
    updateState({
      dataKey: templateMacro.key,
      missingDataIntent: templateMacro.pattern === 'SIMPLE' ? 'none' 
        : templateMacro.pattern === 'FALLBACK' ? 'fallback'
        : 'conditional',
      fallbackValue: templateMacro.fallbackValue || '',
      conditionalContent: templateMacro.conditionalContent || '',
      elseContent: templateMacro.elseContent || '',
      description: templateMacro.description || template.description,
      customSyntax: templateMacro.customSyntax || '',
      advancedMode: templateMacro.pattern === 'ADVANCED',
      currentStep: 5 // Go to preview
    })
  }

  // Add macro handler
  const handleAddMacro = () => {
    const macroConfig = buildMacroFromWizard(wizardState)
    const macro: Macro = {
      ...macroConfig,
      id: generateMacroId()
    }
    
    onAddMacro(macro)
    
    // Reset wizard
    setWizardState({
      currentStep: 1,
      platform: null,
      messageContext: null,
      macroPattern: null,
      startWithTemplate: false,
      selectedTemplate: null,
      dataKey: '',
      missingDataIntent: 'none',
      fallbackValue: '',
      conditionalContent: '',
      elseContent: '',
      description: '',
      advancedMode: false,
      customSyntax: '',
      // Reset pattern-specific data structure
      patternData: {
        simple: {
          dataKey: ''
        },
        fallback: {
          dataKey: '',
          fallbackValue: ''
        },
        conditional: {
          dataKey: '',
          conditionalContent: ''
        },
        conditionalElse: {
          dataKey: '',
          conditionalContent: '',
          elseContent: ''
        },
        advanced: {
          mode: 'guided',
          customSyntax: '',
          guidedConfig: {
            dataKeys: [''],
            operators: [],
            conditions: []
          }
        }
      }
    })
  }

  // Step indicators
  const getStepInfo = () => {
    const steps = [
      { number: 1, label: 'Platform', visible: true },
      { number: 2, label: 'Context', visible: true },
      { number: 3, label: 'Pattern', visible: wizardState.platform !== Platform.SHOPIFY && !wizardState.startWithTemplate },
      { number: 4, label: wizardState.startWithTemplate ? 'Template' : 'Data Key', visible: true },
      { number: 5, label: 'Handling', visible: !wizardState.startWithTemplate && !wizardState.advancedMode && wizardState.macroPattern === null },
      { number: 6, label: 'Preview', visible: !wizardState.advancedMode },
      { number: 7, label: 'Advanced', visible: wizardState.advancedMode }
    ]
    
    return steps.filter(s => s.visible)
  }

  const visibleSteps = getStepInfo()
  const currentStepIndex = visibleSteps.findIndex(s => s.number === wizardState.currentStep)
  const totalSteps = visibleSteps.length

  // Build macro for preview
  const buildCurrentMacro = (): Omit<Macro, 'id'> => {
    return buildMacroFromWizard(wizardState)
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Wizard Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#FE4215]/10 rounded-lg">
              <Sparkles className="w-6 h-6 text-[#FE4215]" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#1A2B49]">Macro Builder</h3>
              <p className="text-sm text-[#6E6D7A]">
                Step {currentStepIndex + 1} of {totalSteps}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative">
          <div className="flex items-center justify-between mb-2">
            {visibleSteps.map((step, index) => (
              <div
                key={step.number}
                className="flex flex-col items-center flex-1"
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    index <= currentStepIndex
                      ? 'bg-[#FE4215] text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step.number}
                </div>
                <span className="text-xs mt-1 text-[#6E6D7A] text-center">
                  {step.label}
                </span>
              </div>
            ))}
          </div>
          <div className="absolute top-4 left-0 right-0 h-1 bg-gray-200 -z-10" />
          <div
            className="absolute top-4 left-0 h-1 bg-[#FE4215] transition-all duration-300 -z-10"
            style={{ width: `${(currentStepIndex / (totalSteps - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          {wizardState.currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <PlatformStep
                selectedPlatform={wizardState.platform}
                onSelect={(platform: Platform) => updateState({ 
                  platform,
                  // Clear context selection when platform changes
                  messageContext: null,
                  startWithTemplate: false,
                  selectedTemplate: null
                })}
              />
            </motion.div>
          )}

          {wizardState.currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <ContextStep
                platform={wizardState.platform}
                selectedContext={wizardState.messageContext}
                onSelectContext={(context) => updateState({ messageContext: context, startWithTemplate: false })}
                onStartWithTemplate={() => {
                  updateState({ startWithTemplate: true })
                  goToStep(4) // Navigate directly to template selection
                }}
              />
            </motion.div>
          )}

          {wizardState.currentStep === 3 && wizardState.platform !== Platform.SHOPIFY && !wizardState.startWithTemplate && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <PatternSelectionStep
                selectedPattern={wizardState.macroPattern}
                onSelectPattern={(pattern) => {
                  if (pattern === 'ADVANCED') {
                    updateState({ macroPattern: pattern, currentStep: 4 })
                  } else {
                    updateState({ macroPattern: pattern })
                  }
                }}
                onBack={() => goToStep(2)}
              />
            </motion.div>
          )}

          {wizardState.currentStep === 4 && wizardState.startWithTemplate && (
            <motion.div
              key="step4a"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <TemplateStep
                messageContext={wizardState.messageContext}
                selectedTemplate={wizardState.selectedTemplate}
                onSelectTemplate={(templateId) => updateState({ selectedTemplate: templateId })}
                onBack={() => {
                  updateState({ startWithTemplate: false, selectedTemplate: null })
                  goToStep(2)
                }}
                onSkipToCustom={() => {
                  updateState({ startWithTemplate: false, selectedTemplate: null })
                }}
              />
            </motion.div>
          )}

          {wizardState.currentStep === 4 && !wizardState.startWithTemplate && (
            <motion.div
              key="step4b"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {/* Pattern-specific step routing */}
              {wizardState.platform === Platform.SHOPIFY ? (
                // Shopify still uses the old DataKeyStep (no pattern selection)
                <DataKeyStep
                  platform={wizardState.platform}
                  dataKey={wizardState.dataKey}
                  onDataKeyChange={(key) => updateState({ dataKey: key })}
                  onBack={() => goToStep(2)}
                />
              ) : wizardState.macroPattern === 'SIMPLE' ? (
                <SimplePatternStep
                  wizardState={wizardState}
                  onUpdateState={updateState}
                  onBack={() => goToStep(3)}
                />
              ) : wizardState.macroPattern === 'FALLBACK' ? (
                <FallbackPatternStep
                  wizardState={wizardState}
                  onUpdateState={updateState}
                  onBack={() => goToStep(3)}
                />
              ) : wizardState.macroPattern === 'CONDITIONAL' ? (
                <ConditionalPatternStep
                  wizardState={wizardState}
                  onUpdateState={updateState}
                  onBack={() => goToStep(3)}
                />
              ) : wizardState.macroPattern === 'CONDITIONAL_ELSE' ? (
                <ConditionalElsePatternStep
                  wizardState={wizardState}
                  onUpdateState={updateState}
                  onBack={() => goToStep(3)}
                />
              ) : wizardState.macroPattern === 'ADVANCED' ? (
                <AdvancedPatternStep
                  wizardState={wizardState}
                  onUpdateState={updateState}
                  onBack={() => goToStep(3)}
                />
              ) : (
                // Fallback to old DataKeyStep if no pattern selected
                <DataKeyStep
                  platform={wizardState.platform}
                  dataKey={wizardState.dataKey}
                  onDataKeyChange={(key) => updateState({ dataKey: key })}
                  onBack={() => goToStep(3)}
                />
              )}
            </motion.div>
          )}

          {wizardState.currentStep === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <MissingDataStep
                dataKey={wizardState.dataKey}
                missingDataIntent={wizardState.missingDataIntent}
                fallbackValue={wizardState.fallbackValue}
                conditionalContent={wizardState.conditionalContent}
                elseContent={wizardState.elseContent}
                onIntentChange={(intent) => updateState({ missingDataIntent: intent })}
                onFallbackChange={(value) => updateState({ fallbackValue: value })}
                onConditionalContentChange={(content) => updateState({ conditionalContent: content })}
                onElseContentChange={(content) => updateState({ elseContent: content })}
                onBack={() => goToStep(4)}
              />
            </motion.div>
          )}

          {wizardState.currentStep === 6 && (
            <motion.div
              key="step6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <PreviewStep
                macro={buildCurrentMacro()}
                onBack={() => {
                  // If pattern was selected, go back to DataKey (skip Handling)
                  if (wizardState.macroPattern !== null) {
                    goToStep(4)
                  } else if (wizardState.startWithTemplate) {
                    goToStep(4)
                  } else {
                    goToStep(5)
                  }
                }}
                onAdvancedEdit={() => updateState({ currentStep: 7, advancedMode: true })}
              />
            </motion.div>
          )}

          {wizardState.currentStep === 7 && (
            <motion.div
              key="step7"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <AdvancedStep
                customSyntax={wizardState.customSyntax}
                onCustomSyntaxChange={(syntax) => updateState({ customSyntax: syntax })}
                onBackToGuided={() => updateState({ currentStep: 6, advancedMode: false })}
                dataKey={wizardState.dataKey}
                onDataKeyChange={(dataKey) => updateState({ dataKey })}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation Buttons */}
      <div className="p-6 border-t border-gray-200 flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={wizardState.currentStep === 1}
          className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
            wizardState.currentStep === 1
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-[#1A2B49] hover:bg-gray-100'
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        {/* For Shopify on step 2 with selected context, show "Start Over" instead of "Continue" */}
        {wizardState.platform === Platform.SHOPIFY && wizardState.currentStep === 2 && wizardState.messageContext ? (
          <button
            onClick={resetWizard}
            className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all bg-[#FE4215] hover:bg-[#E5350A] text-white shadow-sm hover:shadow-md"
          >
            <RefreshCw className="w-5 h-5" />
            <span>Start Over</span>
          </button>
        ) : (
          <button
            onClick={handleNext}
            disabled={!canGoNext()}
            className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
              canGoNext()
                ? 'bg-[#FE4215] hover:bg-[#E5350A] text-white shadow-sm hover:shadow-md'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {wizardState.currentStep === 5 || wizardState.currentStep === 6 ? (
              <>
                <Plus className="w-5 h-5" />
                <span>Add to Macro List</span>
              </>
            ) : (
              <>
                <span>Continue</span>
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

export default MacroWizard
